import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilCalendar, cilLink, cilWarning } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  InfoBox,
  MiniStepper,
  StatTile,
} from 'src/components/common/ImportWizard'
import ClubStatsService from 'src/service/ClubStatsService'
import { formatDate } from './FixturesImporter'
import { LoadingBlock, errorsOf, rowStyle, tableHeadStyle } from './clubstatsShared'

const STEPS = ['Validate', 'Review & Import']

const GROUPS = {
  weekend: {
    label: 'One round per weekend',
    help: 'Each weekend is a round. A two-day match belongs to the round it starts in.',
  },
  match: {
    label: 'Keep two-day matches in one round',
    help: 'A round runs until its two-day matches finish, taking in the one-day games played meanwhile.',
  },
}

// "SMCC 6th XI - One Day Twos" → "One Day Twos"; "SMCC 1st XI" stays as it is.
const shortTeam = (name) => String(name || '').replace(/^.*?-\s*/, '') || name

// "Sat 4 Oct" - the year is already on the round window.
const shortDay = (date) => {
  const d = new Date(`${date}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

/**
 * Rounds from ClubStats. Its fixture list has no round numbers that hold across grades, so
 * rounds are built from when the club's matches are played. Only new rounds are created,
 * through the Round Import's own save; a proposed round that overlaps one the game already has
 * is shown but left alone.
 */
const RoundsImporter = ({ teamIds, onImported }) => {
  const [group, setGroup] = useState('weekend')
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [loadErrors, setLoadErrors] = useState([])
  const [step, setStep] = useState(1)
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [result, setResult] = useState(null)

  const load = useCallback(() => {
    setData(null)
    setLoadErrors([])
    setResult(null)
    setImportErrors([])
    setStep(1)
    ClubStatsService.preview('rounds', { team_ids: teamIds, group })
      .then((res) => {
        if (!res.success) {
          setLoadErrors(errorsOf(res))
          return
        }
        setData(res.data)
        setRows(
          (res.data.items || []).map((item, i) => ({
            key: `${item.start_date}-${i}`,
            item,
            round: item.suggested_round ? String(item.suggested_round) : '',
            selected: !item.existing.length,
          })),
        )
      })
      .catch(() => setLoadErrors(['ClubStats fixtures could not be loaded. Please try again.']))
  }, [teamIds, group])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (key, changes) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))

  const checked = useMemo(() => {
    const taken = new Set((data?.existing_rounds || []).map((r) => String(r.round)))
    const counts = new Map()
    rows.forEach((row) => {
      if (row.selected && row.round) counts.set(row.round, (counts.get(row.round) || 0) + 1)
    })
    return rows.map((row) => {
      let problem = null
      if (!row.item.existing.length && row.selected) {
        if (!/^\d+$/.test(row.round) || Number(row.round) < 1) problem = 'Enter a round number.'
        else if (taken.has(row.round)) problem = `Your game already has a round ${row.round}.`
        else if (counts.get(row.round) > 1) problem = `Round ${row.round} is used twice.`
      }
      const tone =
        row.item.existing.length || !row.selected ? 'skipped' : problem ? 'attention' : 'ready'
      return { ...row, problem, tone }
    })
  }, [rows, data])

  const counts = useMemo(() => {
    const c = { all: checked.length, create: 0, attention: 0, covered: 0, overlap: 0 }
    checked.forEach((row) => {
      if (row.item.existing.length) {
        if (row.item.covered) c.covered++
        else c.overlap++
      } else if (row.selected) {
        c.create++
        if (row.problem) c.attention++
      }
    })
    return c
  }, [checked])

  const toImport = checked.filter((row) => row.selected && !row.item.existing.length)

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const res = await ClubStatsService.runImport('rounds', {
        rows: toImport.map((row) => ({
          round: Number(row.round),
          start_date: row.item.start_date,
          start_time: row.item.start_time,
          end_date: row.item.end_date,
          end_time: row.item.end_time,
        })),
      })
      if (res.success) {
        ToastComponent(res.message || 'Rounds imported.', 'success')
        setResult(res.message)
        if (onImported) onImported()
      } else {
        setImportErrors(errorsOf(res))
      }
    } catch (e) {
      setImportErrors(['The import could not be run. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  const groupPicker = (
    <div className="mb-3" style={{ maxWidth: 420 }}>
      <CFormLabel className="small fw-semibold">
        How should matches be grouped into rounds?
      </CFormLabel>
      <CFormSelect value={group} onChange={(e) => setGroup(e.target.value)}>
        {Object.entries(GROUPS).map(([key, g]) => (
          <option key={key} value={key}>
            {g.label}
          </option>
        ))}
      </CFormSelect>
      <div className="small text-medium-emphasis mt-1">{GROUPS[group].help}</div>
    </div>
  )

  if (loadErrors.length) {
    return (
      <>
        <ImportErrorAlert errors={loadErrors} title="ClubStats data could not be loaded:" />
        <CButton color="primary" variant="outline" onClick={load}>
          Try again
        </CButton>
      </>
    )
  }
  if (!data) return <LoadingBlock text="Working out rounds from the match dates…" />
  if (result) {
    return (
      <>
        <InfoBox color="success">
          {result} Fixtures imported from now on will each find their round.
        </InfoBox>
        <CButton color="primary" variant="outline" onClick={load}>
          Review rounds again
        </CButton>
      </>
    )
  }

  const matchesCell = (item) => (
    <div className="d-flex flex-wrap gap-1">
      {item.matches.map((m) => (
        <CBadge
          key={m.match_id}
          color="light"
          textColor="dark"
          className="fw-normal text-start"
          title={`${m.team_name} vs ${m.opponent} · ${m.grade_name} · ${m.match_type_name}`}
        >
          {shortTeam(m.team_name)} · {shortDay(m.start_date)}
        </CBadge>
      ))}
    </div>
  )

  const statusCell = (row) => {
    if (row.item.existing.length) {
      const numbers = row.item.existing.map((r) => r.round).join(', ')
      return row.item.covered ? (
        <CBadge color="success" shape="rounded-pill" className="fw-normal">
          Covered by your round {numbers}
        </CBadge>
      ) : (
        <>
          <CBadge color="warning" className="fw-normal">
            Overlaps your round {numbers}
          </CBadge>
          <div className="small text-medium-emphasis mt-1">
            Left as it is - adjust it on the Rounds page if its dates should change.
          </div>
        </>
      )
    }
    return (
      <div className="d-flex align-items-center gap-2">
        <CFormCheck
          checked={row.selected}
          onChange={(e) => updateRow(row.key, { selected: e.target.checked })}
          label="Create round"
        />
        <CFormInput
          size="sm"
          type="number"
          min={1}
          value={row.round}
          disabled={!row.selected}
          invalid={Boolean(row.problem)}
          onChange={(e) => updateRow(row.key, { round: e.target.value })}
          style={{ width: 80 }}
        />
      </div>
    )
  }

  const renderValidate = () => (
    <>
      <InfoBox color="light">
        These rounds are worked out from when your club&rsquo;s ClubStats fixtures are played, so
        one round covers every grade playing that weekend. A fixture belongs to the round its start
        falls in.
      </InfoBox>
      {groupPicker}
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="New rounds" value={counts.create} tone="success" icon={cilCalendar} />
        <StatTile
          label="Needs attention"
          value={counts.attention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already covered" value={counts.covered} tone="info" icon={cilLink} />
        {counts.overlap > 0 && (
          <StatTile
            label="Overlap existing"
            value={counts.overlap}
            tone="secondary"
            icon={cilWarning}
          />
        )}
      </div>
      {!rows.length ? (
        <InfoBox color="light">ClubStats has no scheduled fixtures here.</InfoBox>
      ) : (
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" hover className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">Round window</CTableHeaderCell>
                  <CTableHeaderCell>ClubStats fixtures</CTableHeaderCell>
                  <CTableHeaderCell>Round</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {checked.map((row) => (
                  <CTableRow key={row.key} style={rowStyle(row.tone)}>
                    <CTableDataCell className="ps-3" style={{ minWidth: 220 }}>
                      <div className="fw-semibold">
                        {formatDate(row.item.start_date, row.item.start_time)}
                      </div>
                      <div className="small text-medium-emphasis">
                        to {formatDate(row.item.end_date, row.item.end_time)}
                      </div>
                      {row.problem && <div className="small text-warning mt-1">{row.problem}</div>}
                    </CTableDataCell>
                    <CTableDataCell>{matchesCell(row.item)}</CTableDataCell>
                    <CTableDataCell>{statusCell(row)}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
      )}
      <div className="d-flex justify-content-end gap-2">
        <CButton color="secondary" variant="outline" onClick={load}>
          Reload from ClubStats
        </CButton>
        <CButton
          color="primary"
          disabled={counts.attention > 0 || !toImport.length}
          onClick={() => setStep(2)}
        >
          Review {toImport.length} {toImport.length === 1 ? 'round' : 'rounds'}
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <InfoBox color="light">
        These rounds are created with the same checks as the Round Import: no overlaps, no repeated
        numbers, and nothing beyond your plan&rsquo;s season.
        {!data.existing_rounds.length &&
          ' They are your first rounds, so the first one also sets your season start date.'}
      </InfoBox>
      <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
        <div className="table-responsive">
          <CTable align="middle" className="mb-0">
            <CTableHead className="align-middle" style={tableHeadStyle}>
              <CTableRow>
                <CTableHeaderCell className="ps-3">Round</CTableHeaderCell>
                <CTableHeaderCell>Starts</CTableHeaderCell>
                <CTableHeaderCell>Ends</CTableHeaderCell>
                <CTableHeaderCell>Matches</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {toImport.map((row) => (
                <CTableRow key={row.key}>
                  <CTableDataCell className="ps-3 fw-semibold">{row.round}</CTableDataCell>
                  <CTableDataCell>
                    {formatDate(row.item.start_date, row.item.start_time)}
                  </CTableDataCell>
                  <CTableDataCell>
                    {formatDate(row.item.end_date, row.item.end_time)}
                  </CTableDataCell>
                  <CTableDataCell>{row.item.matches.length}</CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>
      <ImportErrorAlert errors={importErrors} />
      <div className="d-flex justify-content-end gap-2">
        <CButton color="secondary" variant="outline" onClick={() => setStep(1)}>
          Back
        </CButton>
        <CLoadingButton color="primary" loading={importing} onClick={runImport}>
          Import {toImport.length} {toImport.length === 1 ? 'round' : 'rounds'}
        </CLoadingButton>
      </div>
    </>
  )

  return (
    <>
      <MiniStepper steps={STEPS} current={step} maxReached={step} onStepClick={setStep} />
      {step === 1 ? renderValidate() : renderReview()}
    </>
  )
}

export default RoundsImporter
